import { textoDaResposta, visivel, type ModeloDeAnamnese, type RespostasDaAnamnese, type SecaoDaAnamnese } from "@bookdental/schemas";
import { cn } from "../lib/cn";
import { PerguntaDaAnamnese } from "./PerguntaDaAnamnese";

/**
 * Seções da anamnese, controladas: quem usa guarda as respostas e decide quando salvar. `secoes`
 * filtra (a página do paciente mostra uma por vez); `pendentes` destaca o que falta responder.
 */
export function FormularioDeAnamnese({
  modelo,
  respostas,
  onChange,
  secoes,
  pendentes,
  desabilitado,
}: {
  modelo: ModeloDeAnamnese;
  respostas: RespostasDaAnamnese;
  onChange: (r: RespostasDaAnamnese) => void;
  secoes?: string[];
  pendentes?: Set<string>;
  desabilitado?: boolean;
}) {
  const mudar = (id: string, r: RespostasDaAnamnese[string] | undefined) => {
    const proximas = { ...respostas };
    if (r) proximas[id] = r;
    else delete proximas[id];
    onChange(proximas);
  };
  const lista = secoes ? modelo.secoes.filter((s) => secoes.includes(s.id)) : modelo.secoes;
  return (
    <div className="space-y-6">
      {lista.map((s) => (
        <SecaoDoFormulario key={s.id} secao={s} respostas={respostas} pendentes={pendentes} desabilitado={desabilitado} onMudar={mudar} />
      ))}
    </div>
  );
}

function SecaoDoFormulario({
  secao,
  respostas,
  pendentes,
  desabilitado,
  onMudar,
}: {
  secao: SecaoDaAnamnese;
  respostas: RespostasDaAnamnese;
  pendentes?: Set<string>;
  desabilitado?: boolean;
  onMudar: (id: string, r: RespostasDaAnamnese[string] | undefined) => void;
}) {
  return (
    <section aria-labelledby={`secao-${secao.id}`} className="space-y-1">
      <h3 id={`secao-${secao.id}`} className="text-base font-semibold text-zinc-900">
        {secao.titulo}
      </h3>
      {secao.descricao && <p className="text-sm text-zinc-500">{secao.descricao}</p>}
      <div className="divide-y divide-zinc-100">
        {secao.perguntas
          .filter((p) => visivel(p, respostas))
          .map((p) => (
            <PerguntaDaAnamnese
              key={p.id}
              p={p}
              r={respostas[p.id]}
              pendente={pendentes?.has(p.id)}
              desabilitada={desabilitado}
              onChange={(r) => onMudar(p.id, r)}
            />
          ))}
      </div>
    </section>
  );
}

/** Leitura (revisão da equipe e "conferir antes de enviar"): pergunta → resposta, por seção. */
export function VisualizacaoDaAnamnese({
  modelo,
  respostas,
  destacarSim = true,
}: {
  modelo: ModeloDeAnamnese;
  respostas: RespostasDaAnamnese;
  /** "Sim" com alerta clínico fica em âmbar, pra saltar aos olhos na revisão. */
  destacarSim?: boolean;
}) {
  return (
    <div className="space-y-5">
      {modelo.secoes.map((s) => (
        <section key={s.id} className="space-y-1.5">
          <h4 className="text-sm font-semibold text-zinc-900">{s.titulo}</h4>
          <dl className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            {s.perguntas
              .filter((p) => visivel(p, respostas))
              .map((p) => {
                const texto = textoDaResposta(p, respostas[p.id]);
                const alerta = destacarSim && !!p.alerta && [respostas[p.id]?.valor, ...(respostas[p.id]?.valores ?? [])].some((v) => v && p.alerta!.quando.includes(v));
                return (
                  <div key={p.id} className={cn("grid gap-1 px-3 py-2 text-sm sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-4", alerta && "bg-amber-50")}>
                    <dt className="text-zinc-600">{p.texto}</dt>
                    <dd className={cn(texto ? "text-zinc-900" : "italic text-zinc-400", alerta && "font-semibold text-amber-800")}>{texto ?? "Sem resposta"}</dd>
                  </div>
                );
              })}
          </dl>
        </section>
      ))}
    </div>
  );
}
